// The general terms (VOP) accepted, as their clause 2.1 asks
// (`/api/terms-consent`, `optivo-service/src/routes/termsConsent.ts`).

export interface TermsConsentStatus {
    version: string;
    accepted: boolean;
    acceptedAt: string | null;
    companyId: string | null;
    /** The company ID of the person's latest acceptance of any wording, offered again. */
    previousCompanyId: string | null;
}

export type TermsConsentRead =
    /** Nobody is signed in: the shell's own sign-in check decides what to show. */
    | { kind: "signedOut" }
    /**
     * The server has no such route (a page newer than the service), or did
     * not answer: nothing can be said, and the server's own gate decides.
     */
    | { kind: "unknown" }
    | { kind: "read"; consent: TermsConsentStatus; email: string | null; pendingApproval: boolean };

/**
 * Whether the signed-in person accepted the current general terms. Read with
 * plain `fetch` rather than `apiFetch`: it is asked before the shell knows
 * whether anybody is signed in, and a 401 here is a visitor, not a session
 * that expired.
 */
export async function fetchTermsConsent(): Promise<TermsConsentRead> {
    let res: Response;
    try {
        res = await fetch("/api/terms-consent");
    } catch {
        return { kind: "unknown" };
    }
    if (res.status === 401) return { kind: "signedOut" };
    if (!res.ok) return { kind: "unknown" };
    const data = (await res.json().catch(() => null)) as {
        success?: boolean;
        consent?: TermsConsentStatus;
        email?: string | null;
        pendingApproval?: boolean;
    } | null;
    if (!data?.success || !data.consent || typeof data.consent.accepted !== "boolean") return { kind: "unknown" };
    return { kind: "read", consent: data.consent, email: data.email ?? null, pendingApproval: data.pendingApproval === true };
}

export type TermsConsentRefusal = "TERMS_VERSION_CHANGED" | "INVALID_COMPANY_ID" | "CONSENT_INCOMPLETE";

/** A refusal the screen has words for; anything else is thrown as a plain error. */
export class TermsConsentRefusedError extends Error {
    constructor(readonly code: TermsConsentRefusal) {
        super(code);
        this.name = "TermsConsentRefusedError";
    }
}

const REFUSALS: readonly string[] = ["TERMS_VERSION_CHANGED", "INVALID_COMPANY_ID", "CONSENT_INCOMPLETE"];

export interface AcceptTermsPayload {
    version: string;
    companyId: string;
    accepted: string[];
}

export async function acceptTermsConsent(payload: AcceptTermsPayload): Promise<TermsConsentStatus> {
    const res = await fetch("/api/terms-consent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    const data = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        code?: string;
        error?: string;
        consent?: { version: string; acceptedAt: string; companyId: string };
    };
    if (!res.ok || !data.success || !data.consent) {
        if (data.code && REFUSALS.includes(data.code)) throw new TermsConsentRefusedError(data.code as TermsConsentRefusal);
        throw new Error(data.error || `acceptTermsConsent failed: ${res.status}`);
    }
    return {
        version: data.consent.version,
        accepted: true,
        acceptedAt: data.consent.acceptedAt,
        companyId: data.consent.companyId,
        previousCompanyId: data.consent.companyId,
    };
}
